import { InfiniteQueryDataTableCompass } from "@circle-react-shared/InfiniteQueryDataTableCompass";
import { CollapsibleSearchInput } from "@circle-react/components/Drawers/CustomizeSpaceDrawerCompass/MembersTab/CollapsibleSearchInput";
import { useMemberCSV } from "@circle-react/hooks/members/useMembersCSV";
import { useTailwindMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { SpaceGroup } from "@circle-react/types/SpaceGroup";
import { Button } from "@circleco/compass/components/Button";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { internalApi } from "@/react/helpers/urlHelpers";

import type { RawSpaceGroupMember } from "./helpers";
import { useSpaceGroupMembersTab } from "./hooks/useSpaceGroupMembersTab";
import { SpaceGroupMembersEmptyState } from "./SpaceGroupMembersEmptyState";

interface SpaceGroupMembersCompassProps {
  spaceGroup: SpaceGroup;
}

export const SpaceGroupMembersCompass = ({
  spaceGroup,
}: SpaceGroupMembersCompassProps) => {
  const {
    addMemberButtonLabel,
    columns,
    handleOpenAddMemberModal,
    infiniteQuery,
    isAdmin,
    query,
    rowActions,
    setQuery,
  } = useSpaceGroupMembersTab(spaceGroup);

  const { downloadMembersCSV } = useMemberCSV();
  const isMdScreen = useTailwindMdScreenMediaQuery();

  const shouldShowEmptyState =
    !infiniteQuery.isLoading &&
    !infiniteQuery.isFetching &&
    !query &&
    infiniteQuery.totalQueryMembers === 0;

  const emptyOverlay = query
    ? t("space_groups.form.edit.members.no_members_found_for_term", {
        member_search_term: query,
      })
    : t("settings.manage_members.no_members_found");

  return (
    <div className="flex w-full flex-col gap-4 pb-6">
      {shouldShowEmptyState ? (
        <SpaceGroupMembersEmptyState
          addMemberButtonLabel={addMemberButtonLabel}
          onAddMembers={handleOpenAddMemberModal}
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <CollapsibleSearchInput
              placeholder={t("space_groups.search_members")}
              onChange={setQuery}
            />
            <div
              className={classNames(
                "grid w-full gap-3 md:flex md:w-auto",
                isAdmin ? "grid-cols-2" : "grid-cols-1",
              )}
            >
              {isAdmin && (
                <Button
                  variant="ghost"
                  fullWidth={!isMdScreen}
                  onClick={() =>
                    downloadMembersCSV({
                      downloadURL: internalApi.spaceGroups.exportMembers({
                        id: spaceGroup.id,
                      }),
                    })
                  }
                >
                  {t("download_csv")}
                </Button>
              )}
              <Button
                variant="primary"
                fullWidth={!isMdScreen}
                onClick={handleOpenAddMemberModal}
              >
                {addMemberButtonLabel}
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <InfiniteQueryDataTableCompass<RawSpaceGroupMember>
              key={`${spaceGroup.id}:${query}`}
              infiniteQuery={infiniteQuery}
              columns={columns}
              rowActions={rowActions}
              getRowId={row => String(row.id)}
              emptyOverlay={emptyOverlay}
              rowSize="auto"
            />
          </div>
        </>
      )}
    </div>
  );
};
